import { useMemo } from 'react';
import type { VenueType, PrimaryAtmosphere, Amenity } from '@wineryxplorer/shared';
import { trpc } from '../lib/trpc';
import type { MapBounds } from './useSearchScope';

interface UseVenueMapPinsArgs {
  pendingMapBounds: MapBounds | null;
  initialMapBounds: { north: number; south: number; east: number; west: number } | undefined;
  searchText: string;
  venueTypes: VenueType[];
  atmospheres: PrimaryAtmosphere[];
  amenities: Amenity[];
}

/**
 * Map-pin query for the Wineries search page.
 *
 * Separate from the results query: it uses LIVE pending bounds so pins update
 * as the user pans, before they click Search-this-area to commit. Falls back to
 * the typed-location's natural rect on initial load. Text and filters match the
 * results query, so pins never show venues the list has filtered out.
 */
export const useVenueMapPins = ({
  pendingMapBounds,
  initialMapBounds,
  searchText,
  venueTypes,
  atmospheres,
  amenities,
}: UseVenueMapPinsArgs) => {
  const mapInput = useMemo(
    () => ({
      bounds: pendingMapBounds
        ? {
            north: pendingMapBounds.ne.lat,
            south: pendingMapBounds.sw.lat,
            east: pendingMapBounds.ne.lng,
            west: pendingMapBounds.sw.lng,
          }
        : initialMapBounds,
      query: searchText || undefined,
      filters: {
        venueTypes: venueTypes.length > 0 ? venueTypes : undefined,
        priceRanges: undefined as number[] | undefined,
        primaryAtmospheres: atmospheres.length > 0 ? atmospheres : undefined,
        amenities: amenities.length > 0 ? amenities : undefined,
      },
    }),
    [pendingMapBounds, initialMapBounds, searchText, venueTypes, atmospheres, amenities]
  );

  const { data: mapData } = trpc.venue.getForMap.useQuery(mapInput);

  return useMemo(() => {
    if (!mapData?.items) return [];
    return mapData.items.map((v) => ({
      _id: v.id,
      name: v.name,
      venueType: v.venueType,
      address: {
        city: v.address.city,
        state: v.address.state,
        coordinates: v.coordinates,
      },
      priceRange: null,
      firstHeroImage: null,
      logoImage: null,
    }));
  }, [mapData]);
};
